import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { getAppPath } from '../utils/runtimeMode';
import './CommitteeMeetingsDashboard.css';

// Simple utility to format date/time
function formatDate(d){
  if(!d) return ''; const dt = new Date(d); return dt.toLocaleDateString();
}
function formatTime(t){
  if(!t) return ''; // Snowflake TIME may come as HH:MM:SS
  return t.substring(0,5);
}

// Tab for Attendance Analytics
const AttendanceTab = ({ meetings, loading, error, expanded, toggleExpand, inviteLoading, inviteError }) => {
  if(loading) return <p>Loading meetings…</p>;
  if(error) return <p className="error">Error: {error}</p>;

  return (
    <>
      <h2>Committee Meetings Attendance</h2>
      {meetings.length === 0 && <p>No meetings found.</p>}
      <table className="cm-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>Location</th>
            <th>Agenda</th>
            <th>Attending</th>
            <th>Regrets</th>
            <th>Pending</th>
            <th>Total</th>
            <th>Prev Mtg</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {meetings.map(m => {
            const agendaLink = m.AGENDA_PDF_URL ? <a href={m.AGENDA_PDF_URL} target="_blank" rel="noreferrer">PDF</a> : '—';
            return (
              <React.Fragment key={m.ID}>
                <tr className={expanded[m.ID] ? 'expanded' : ''}>
                  <td>{formatDate(m.MEETING_DATE)}</td>
                  <td>{formatTime(m.MEETING_TIME)}</td>
                  <td>{m.LOCATION}</td>
                  <td>{agendaLink}</td>
                  <td>{m.ATTENDING_COUNT}</td>
                  <td>{m.REGRETS_COUNT}</td>
                  <td>{m.PENDING_COUNT}</td>
                  <td>{m.TOTAL_INVITES}</td>
                  <td>{m.PREVIOUS_MEETING_ID ? m.PREVIOUS_MEETING_ID.substring(0,8)+'…' : '—'}</td>
                  <td>
                    <button onClick={() => toggleExpand(m.ID)} className="cm-btn">
                      {expanded[m.ID] ? 'Hide' : 'View'}
                    </button>
                  </td>
                </tr>
                {expanded[m.ID] && (
                  <tr className="invite-row">
                    <td colSpan={10}>
                      {inviteLoading[m.ID] && <p>Loading invites…</p>}
                      {inviteError[m.ID] && <p className="error">{inviteError[m.ID]}</p>}
                      {!inviteLoading[m.ID] && !inviteError[m.ID] && (
                        <table className="cm-subtable">
                          <thead>
                            <tr>
                              <th>Name</th><th>Email</th><th>Department</th><th>Status</th><th>Responded At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expanded[m.ID].map(inv => (
                              <tr key={inv.MEMBER_EMAIL+inv.STATUS+inv.RESPONDED_AT}>
                                <td>{inv.MEMBER_NAME || '—'}</td>
                                <td>{inv.MEMBER_EMAIL}</td>
                                <td>{inv.DEPARTMENT || '—'}</td>
                                <td className={`status-${inv.STATUS}`}>{inv.STATUS}</td>
                                <td>{inv.RESPONDED_AT ? new Date(inv.RESPONDED_AT).toLocaleString() : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

// Tab for Minutes Library
const MinutesLibraryTab = () => {
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function loadMinutes() {
      setLoading(true);
      setError(null);
      try {
        const res = await API.get('/api/committeeMeetings/minutes/library');
        if (res.data && res.data.success) {
          if (mounted) setMinutes(res.data.minutes || []);
        } else {
          throw new Error(res.data.error || 'Failed to load minutes');
        }
      } catch (e) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadMinutes();
    return () => { mounted = false; };
  }, []);

  if (loading) return <p>Loading minutes library…</p>;
  if (error) return <p className="error">Error: {error}</p>;

  return (
    <>
      <h2>Meeting Minutes Library</h2>
      {minutes.length === 0 && <p>No finalized meeting minutes found.</p>}
      <table className="cm-table">
        <thead>
          <tr>
            <th>Meeting Date</th>
            <th>Time</th>
            <th>Location</th>
            <th>Quorum</th>
            <th>Attended</th>
            <th>Regrets</th>
            <th>Agenda Adopted</th>
            <th>Prev Minutes Adopted</th>
            <th>Finalized</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {minutes.map(m => (
            <tr key={m.minutesId}>
              <td>{formatDate(m.meetingDate)}</td>
              <td>{formatTime(m.meetingTime)}</td>
              <td>{m.location || '—'}</td>
              <td className={m.quorumMet ? 'status-attending' : 'status-regrets'}>
                {m.quorumMet ? '✓ Met' : '✗ Not Met'}
              </td>
              <td>{m.attendingCount}</td>
              <td>{m.regretsCount}</td>
              <td>{m.adoptAgenda ? '✓' : '—'}</td>
              <td>{m.adoptPreviousMinutes ? '✓' : '—'}</td>
              <td>{formatDate(m.finalizedAt)}</td>
              <td>
                <a 
                  href={getAppPath(`/committee-meeting?type=minutes&agendaId=${m.meetingId}`)}
                  className="cm-btn"
                  style={{ textDecoration: 'none', display: 'inline-block' }}
                >
                  View
                </a>
                {m.agendaPdfUrl && (
                  <a 
                    href={m.agendaPdfUrl} 
                    target="_blank" 
                    rel="noreferrer"
                    className="cm-btn cm-btn-secondary"
                    style={{ marginLeft: '8px', textDecoration: 'none', display: 'inline-block' }}
                  >
                    Agenda
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Summary Stats */}
      {minutes.length > 0 && (
        <div className="cm-stats">
          <h3>Summary Statistics</h3>
          <div className="cm-stats-grid">
            <div className="cm-stat-card">
              <span className="cm-stat-value">{minutes.length}</span>
              <span className="cm-stat-label">Total Meetings</span>
            </div>
            <div className="cm-stat-card">
              <span className="cm-stat-value">
                {minutes.filter(m => m.quorumMet).length}
              </span>
              <span className="cm-stat-label">Quorum Met</span>
            </div>
            <div className="cm-stat-card">
              <span className="cm-stat-value">
                {Math.round(minutes.reduce((sum, m) => sum + (m.attendingCount || 0), 0) / minutes.length)}
              </span>
              <span className="cm-stat-label">Avg Attendance</span>
            </div>
            <div className="cm-stat-card">
              <span className="cm-stat-value">
                {minutes.filter(m => m.hasInspectionReports).length}
              </span>
              <span className="cm-stat-label">With Inspections</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CommitteeMeetingsDashboard = () => {
  const [activeTab, setActiveTab] = useState('attendance');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // meetingId -> invite list
  const [inviteLoading, setInviteLoading] = useState({}); // meetingId -> boolean
  const [inviteError, setInviteError] = useState({}); // meetingId -> error

  useEffect(() => {
    let mounted = true;
    async function load(){
      setLoading(true); setError(null);
      try {
        const res = await API.get('/api/committeeMeetings/list');
        if(res.data && res.data.success){
          if(mounted) setMeetings(res.data.meetings || []);
        } else {
          throw new Error(res.data.error || 'Failed to load meetings');
        }
      } catch(e){
        if(mounted) setError(e.message);
      } finally {
        if(mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const toggleExpand = async (meetingId) => {
    if(expanded[meetingId]){ // collapse
      setExpanded(prev => { const copy = { ...prev }; delete copy[meetingId]; return copy; });
      return;
    }
    setInviteLoading(prev => ({ ...prev, [meetingId]: true }));
    setInviteError(prev => ({ ...prev, [meetingId]: null }));
    try {
      const res = await API.get(`/api/committeeMeetings/meetings/${meetingId}/invites`);
      if(res.data && res.data.success){
        setExpanded(prev => ({ ...prev, [meetingId]: res.data.invites || [] }));
      } else {
        throw new Error(res.data.error || 'Failed to load invites');
      }
    } catch(e){
      setInviteError(prev => ({ ...prev, [meetingId]: e.message }));
    } finally {
      setInviteLoading(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  if(loading && activeTab === 'attendance') return <div className="cm-dashboard"><p>Loading meetings…</p></div>;
  if(error && activeTab === 'attendance') return <div className="cm-dashboard error">Error: {error}</div>;

  return (
    <div className="cm-dashboard">
      {/* Tab Navigation */}
      <div className="cm-tabs">
        <button 
          className={`cm-tab ${activeTab === 'attendance' ? 'cm-tab-active' : ''}`}
          onClick={() => setActiveTab('attendance')}
        >
          📊 Attendance Analytics
        </button>
        <button 
          className={`cm-tab ${activeTab === 'minutes' ? 'cm-tab-active' : ''}`}
          onClick={() => setActiveTab('minutes')}
        >
          📄 Minutes Library
        </button>
      </div>

      {/* Tab Content */}
      <div className="cm-tab-content">
        {activeTab === 'attendance' && (
          <AttendanceTab 
            meetings={meetings}
            loading={loading}
            error={error}
            expanded={expanded}
            toggleExpand={toggleExpand}
            inviteLoading={inviteLoading}
            inviteError={inviteError}
          />
        )}
        {activeTab === 'minutes' && <MinutesLibraryTab />}
      </div>

      <p className="cm-note">Counts auto-refresh on page load; re-open the page to refresh.</p>
    </div>
  );
};

export default CommitteeMeetingsDashboard;